import React from 'react';
import Icon from '../../../components/AppIcon';

const BlogSection = () => {
  const blogs = [
    {
      badge: "Partnership",
      badgeColor: "bg-cyan-500/20 text-cyan-300 border-cyan-500/30",
      date: "July 15, 2026",
      readTime: "5 min read",
      title: "Amplifying Innovation: Our Strategic Media Partnerships",
      excerpt: "How Orator Hype is collaborating with premier media networks like CNBC and Forbes to share stories of digital transformation, technological breakthroughs, and the future of branding.",
      content: "In today's hyper-connected landscape, aligning technical innovation with public media presence is essential. Orator Hype is proud to partner with leading media outlets to publish insightful columns on Cloud Infrastructure, cybersecurity preparedness, and modern branding alignments. By sharing real-world case studies and industry best practices, we aim to inspire the next generation of digital leaders and demonstrate the transformative power of integrated IT solutions.",
      icon: "Newspaper",
      iconColor: "text-cyan-400 bg-cyan-500/10",
      author: {
        name: "Marketing Team",
        role: "Brand & Comms Specialist",
        avatar: "MT"
      }
    },
    {
      badge: "Artificial Intelligence",
      badgeColor: "bg-purple-500/20 text-purple-300 border-purple-500/30",
      date: "July 18, 2026",
      readTime: "7 min read",
      title: "Coding at the Speed of Thought: The AI Influence on Modern Software Engineering",
      excerpt: "An in-depth look at how generative AI and agentic coding platforms are accelerating software development lifecycles, boosting code quality, and elevating developer productivity.",
      content: "Software development is undergoing a seismic shift. Generative AI is moving beyond simple code completions to fully agentic systems capable of planning, testing, and debugging. At Orator Hype, we leverage state-of-the-art AI tooling to streamline boilerplate creation, draft test suites, and audit code safety. Our engineers leverage these intelligent assistants to accelerate delivery timeframes, freeing up mental bandwidth to design resilient system architectures and craft delightful user experiences.",
      icon: "Cpu",
      iconColor: "text-purple-400 bg-purple-500/10",
      author: {
        name: "Engineering Lead",
        role: "Head of Technology",
        avatar: "EL"
      }
    }
  ];

  return (
    <section className="relative overflow-hidden py-16 md:py-24 lg:py-32 px-4 md:px-6 lg:px-8 border-t border-b border-white/5 bg-gradient-to-br from-slate-900 via-indigo-950 to-slate-900 text-white">
      {/* Background decoration */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-40 -left-40 w-96 h-96 bg-cyan-500/10 rounded-full blur-3xl"></div>
        <div className="absolute -bottom-40 -right-40 w-96 h-96 bg-purple-500/10 rounded-full blur-3xl"></div>
      </div>

      <div className="relative z-10 max-w-7xl mx-auto">
        {/* Header */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center gap-2 bg-white/10 px-4 py-2 rounded-full mb-4 border border-white/20">
            <Icon name="BookOpen" size={18} className="text-cyan-400" />
            <span className="text-sm font-medium">Insights & Perspectives</span>
          </div>

          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-6">
            Latest From Our{" "}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-purple-400">
              Blog
            </span>
          </h2>

          <p className="text-white/70 max-w-3xl mx-auto text-base md:text-lg">
            Stay updated with our latest thoughts on partnerships, industry breakthroughs, and how cutting-edge technologies are reshaping the digital landscape.
          </p>
        </div>

        {/* Blog Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 md:gap-10">
          {blogs.map((blog, index) => (
            <div
              key={index}
              className="group flex flex-col justify-between h-full bg-white/5 backdrop-blur-lg rounded-3xl p-6 md:p-8 border border-white/10 hover:border-cyan-500/30 hover:bg-white/10 transition-all duration-300 shadow-xl hover:shadow-2xl hover:shadow-cyan-500/5"
            >
              <div>
                {/* Badge and Metadata */}
                <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
                  <span className={`px-3 py-1 rounded-full text-xs font-semibold border ${blog.badgeColor}`}>
                    {blog.badge}
                  </span>
                  <div className="flex items-center gap-4 text-xs text-white/60">
                    <span className="flex items-center gap-1.5">
                      <Icon name="Calendar" size={14} className="text-white/40" />
                      {blog.date}
                    </span>
                    <span className="flex items-center gap-1.5">
                      <Icon name="Clock" size={14} className="text-white/40" />
                      {blog.readTime}
                    </span>
                  </div>
                </div>

                {/* Blog Header Icon + Title */}
                <div className="flex items-start gap-4 mb-5">
                  <div className={`p-3 rounded-2xl ${blog.iconColor} flex-shrink-0 group-hover:scale-110 transition-transform duration-300`}>
                    <Icon name={blog.icon} size={24} />
                  </div>
                  <h3 className="text-xl md:text-2xl font-bold group-hover:text-cyan-400 transition-colors duration-300 leading-snug">
                    {blog.title}
                  </h3>
                </div>

                {/* Excerpt and Content */}
                <p className="text-cyan-300/80 text-sm font-medium mb-4 leading-relaxed">
                  {blog.excerpt}
                </p>
                
                <p className="text-white/70 text-sm md:text-base leading-relaxed mb-8">
                  {blog.content}
                </p>
              </div>

              {/* Author & Footer */}
              <div className="border-t border-white/10 pt-6 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-gradient-to-br from-cyan-500 to-indigo-600 flex items-center justify-center font-bold text-sm text-white shadow-md">
                    {blog.author.avatar}
                  </div>
                  <div>
                    <h4 className="font-semibold text-sm">{blog.author.name}</h4>
                    <p className="text-xs text-white/50">{blog.author.role}</p>
                  </div>
                </div>
                
                <div className="flex items-center gap-1.5 text-xs font-semibold text-cyan-400 group-hover:translate-x-1.5 transition-transform duration-300 cursor-pointer">
                  <span>Read Article</span>
                  <Icon name="ArrowRight" size={16} />
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default BlogSection;
